import { Fragment, useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingCalendly from '../components/FloatingCalendly';
import Head from 'next/head';
import Script from 'next/script';
import { DemoPopupProvider } from '../context/DemoPopupContext';

import '../styles/globals.css';

const GTM_ID = 'GTM-TC4BQMH4';

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [locale, setLocale] = useState('en');
  const [loaded, setLoaded] = useState(false);
  const mixpanelRef = useRef(null);

  // Initialise analytics once. The dynamic import resolves to a module
  // namespace, so use `.default` to get the real Mixpanel instance — calling
  // track() on the namespace loses `this` and throws internally.
  // Deferred to idle time: mixpanel's init does DOM-scaling work that measured
  // 2.4s+ of main-thread blocking on pages with large DOMs (e.g. the
  // homepage) when run eagerly on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const initMixpanel = () => {
      import('mixpanel-browser').then((m) => {
        const mixpanel = m.default || m;
        try {
          mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN, {
            track_pageview: true,
          });
          mixpanelRef.current = mixpanel;
        } catch (e) {
          /* analytics is best-effort — never fatal */
        }
      });
    };

    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(initMixpanel, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }

    const id = window.setTimeout(initMixpanel, 1);
    return () => window.clearTimeout(id);
  }, []);

  // Track page views on client-side navigation. Wrapped so a tracking failure
  // can never break navigation; handler is cleaned up to avoid accumulation.
  useEffect(() => {
    // Clarity consent: retried on every navigation (like the original), so it
    // still lands once Clarity's async script has loaded.
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('consent');
    }

    const handleRouteChange = (url) => {
      try {
        mixpanelRef.current?.track?.('Page View', { url, ...router.query });
      } catch (e) {
        /* non-fatal */
      }
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events, router.query]);

  useEffect(() => {
    setLoaded(true);
    setLocale('en');
  }, []);

  // Capture the Google Ads click id (gclid) into a 90-day cookie so leads can
  // be attributed later. Runs on first load and on every client-side route
  // change (window "load" alone would miss SPA navigations).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const captureGclid = () => {
      try {
        const gclid = new URLSearchParams(window.location.search).get('gclid');
        if (gclid) Cookies.set('gclid', gclid, { expires: 90 });
      } catch (e) {
        /* non-fatal */
      }
    };

    captureGclid();
    router.events.on('routeChangeComplete', captureGclid);
    return () => router.events.off('routeChangeComplete', captureGclid);
  }, [router.events]);

  return (
    <DemoPopupProvider>
    <Fragment>
      <Head>
        <meta name='viewport' content='initial-scale=1.0, width=device-width' />
        {locale === 'en' && <meta name="google-site-verification" content="Ar2pOQrCIDG1ORdwo4cU864q2zpB1hHHvXI6HzQJazc" />}
        {locale === 'en' && <meta name="msvalidate.01" content="046BF1AD4DCD49E40AF17F3E9B071245" />}
        {locale === 'es' && <meta name="robots" content="noindex, follow"/>}
        {locale === 'pt' && <meta name="robots" content="noindex, follow"/>}
      </Head>

      {/* GTM (and the gtag.js it injects) is deferred until the page is
          interactive so it never competes with critical-path resources
          for LCP/FCP, and its layout reads happen after first paint. */}
      <Script
        id='gtm-script'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
              });

              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),
                  dl=l !== 'dataLayer' ? '&l=' + l : '';

              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_ID}');
          `,
        }}
      />

      {/* Clarity is a session-recording tool, not needed for first paint —
          lazyOnload loads it once the browser is idle so its 25 KiB (with a
          short third-party cache TTL we don't control) never delays LCP/FCP. */}
      {loaded && (
        <Script
          id='clarity-script'
          strategy='lazyOnload'
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.defer=true;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "icwime9tqa");
            `,
          }}
        />
      )}

      <Navbar locale={locale} />
      <Component {...pageProps} locale={locale} />
      <Footer locale={locale} />
      <FloatingCalendly />
    </Fragment>
    </DemoPopupProvider>
  );
}

export default MyApp;
